import axiosInstance from './axioInstance';
import { UseQueryResult, useQuery } from "@tanstack/react-query";

export const PATHS = Object.freeze({
  GET_MEETINGS: "/meeting/list",
  GET_TEAM_INFO_MEETINGS: "/meeting/team/list",
  GET_CLIENT_ACTION_ITEMS: "/actionitem/team",
  GET_MONTHLY_INSIGHT: "/me/team/monthly-insight",
});

interface GetMeetingsParams {
  start_time: string,
  end_time: string,
  calendar_domain?: string,
  client_domain?: string,
  page: number,
  per_page: number,
  search: string,
  sort: { sort_field: string, sort_order: string },
  impersonate_email?: string;
  is_team_meeting_id?: boolean;
  is_external?: boolean;
  group_type?: string;
}
interface GetMonthlyInsightParams {
  start_time: string,
  end_time: string,
  client_domain?: string,
  impersonate_email?: string;
}
interface GetActionItemParams {
  start_time: string,
  end_time: string,
  client_domains?: string,
  page: number;
  per_page: number;
  search: string,
  status: string
  sort: { sort_field: string, sort_order: string },
  types: string,
  include_me:boolean,
  employee_email_ids: string;
  impersonate_email?: string;
}
const getMeetings = async (params: GetMeetingsParams): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_MEETINGS}`, {
    params: {
      //todo: get it from calendar: google/zoom
      start_time: params.start_time,
      end_time: params.end_time,
      calendar_domain: "google",
      client_domain: params.client_domain,
      page: params.page,
      per_page: params.per_page,
      ...(params.search && { search: params.search }),
      sort_field: params.sort.sort_field,
      sort_order: params.sort.sort_order,
      impersonate_email: params.impersonate_email,
      is_team_meeting_id: params.is_team_meeting_id,
    }
  });
  return response;
};

const getTeamInfoMeetings = async (params: GetMeetingsParams): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_TEAM_INFO_MEETINGS}`, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      calendar_domain: "google",
      client_domain: params.client_domain,
      page: params.page,
      per_page: params.per_page,
      ...(params.search && { search: params.search }),
      sort_field: params.sort.sort_field,
      sort_order: params.sort.sort_order,
      impersonate_email: params.impersonate_email,
      is_team_meeting_id: params.is_team_meeting_id,
    }
  });
  return response;
};

const getMonthlyInsight = async (params: GetMonthlyInsightParams): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_MONTHLY_INSIGHT}`, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      client_domain: params.client_domain,
      impersonate_email: params.impersonate_email || undefined,
    }
  });
  return response;
}

const useGetMonthlyInsight = (params: GetMonthlyInsightParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params)
  return useQuery<string>({
    queryKey: ['get_monthly_insight', stringifiedParams],
    queryFn: () => getMonthlyInsight(params),
    enabled: enabled
  })
}


const getActionItems = async (params: GetActionItemParams): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_CLIENT_ACTION_ITEMS}`, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      client_domains: params.client_domains,
      page: params.page,
      per_page: params.per_page,
      ...(params.search && { search: params.search }),
       status: "todo,in_progress,completed",
      sort_field: params.sort.sort_field,
      sort_order: params.sort.sort_order,
      ...(params.types && { types: params.types }),
      include_me:params.include_me,
      employee_email_ids: params.employee_email_ids,
      ...(params.impersonate_email && { impersonate_email: params.impersonate_email }),
    }
  });
  return response;
};

const getPendingActionItems = async (params: GetActionItemParams): Promise<any> => {
  const response = await axiosInstance.get(`${PATHS.GET_CLIENT_ACTION_ITEMS}`, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      client_domains: params.client_domains,
      page: params.page,
      per_page: params.per_page,
      ...(params.search && { search: params.search }),
      ...(params.status !== undefined && { status: params.status }),
      sort_field: params.sort.sort_field,
      sort_order: params.sort.sort_order,
      ...(params.types && { types: params.types }),
      include_me: params.include_me,
      employee_email_ids: params.employee_email_ids
    }
  });
  return response;
};
const useGetMeetings = (params: GetMeetingsParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params)
  return useQuery<string>({
    queryKey: ['get_meetings', stringifiedParams],
    queryFn: () => getMeetings(params),
    enabled: enabled
  })
}

const useGetTeamInfoMeetings = (params: GetMeetingsParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params)
  return useQuery<string>({
    queryKey: ['get_team_info_meetings', stringifiedParams],
    queryFn: () => getTeamInfoMeetings(params),
    enabled: enabled
  })
}
const useGetActionItem = (params: GetActionItemParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params)
  return useQuery<string>({
    queryKey: ['get_team_action_items', stringifiedParams],
    queryFn: () => getActionItems(params),
    enabled: enabled
  })
}
const useGetPendingActionItem = (params: GetActionItemParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params)
  return useQuery<string>({
    queryKey: ['get_team_action_items', stringifiedParams],
    queryFn: () => getPendingActionItems(params),
    enabled: enabled
  })
}
export interface ClientAPIMeetings {
  readonly useGetMeetings: (params: any, enabled: boolean) => UseQueryResult<any, any>;
  readonly useGetTeamInfoMeetings: (params: GetMeetingsParams, enabled: boolean) => UseQueryResult<any, any>;
  readonly useGetActionItem: (params: any, enabled: boolean) => UseQueryResult<any, any>;
  readonly useGetPendingActionItem: (params: any, enabled: boolean) => UseQueryResult<any, any>;
  readonly useGetMonthlyInsight: (params: any, enabled: boolean) => UseQueryResult<any, any>;

}
const ClientAPIMeetings: ClientAPIMeetings = Object.freeze({
  useGetMeetings,
  useGetTeamInfoMeetings,
  useGetActionItem,
  useGetPendingActionItem,
  useGetMonthlyInsight
});

export default ClientAPIMeetings;